import { useQuery } from '@tanstack/react-query';
import { chunk, uniq, uniqBy } from 'lodash';
import { fetchAssembliesWithoutPagination } from 'src/hooks';
import { ASSEMBLY_ID_CHUNK_SIZE } from 'src/hooks/api/assembly/useGetAllAssemblyComponents';
import { IAssembly } from 'src/interfaces';
import { queryKeys } from 'src/utils';

const fetchAssembliesByIdsChunked = async (
  filterKey: 'id' | 'variation_parent_id',
  ids: number[],
): Promise<IAssembly[]> => {
  if (ids.length === 0) {
    return [];
  }

  const idChunks = chunk(ids, ASSEMBLY_ID_CHUNK_SIZE);
  const responses = await Promise.all(
    idChunks.map((c) =>
      fetchAssembliesWithoutPagination([`${filterKey}.list=${c.join('|')}`]),
    ),
  );

  return responses.flat();
};

export const useFetchAssembliesWithVariations = (assembliesIds: number[]) => {
  const sortedIds = [...assembliesIds].sort((a, b) => a - b);
  const assembliesIdsJoinString = sortedIds.join('|');

  const { data: selectedAssemblies = [] } = useQuery({
    queryKey: queryKeys.assemblies.all([
      `id.list=${assembliesIdsJoinString}`,
      'pageSize=0',
    ]),
    queryFn: () => fetchAssembliesByIdsChunked('id', sortedIds),
    staleTime: Infinity,
    enabled: sortedIds.length > 0,
  });

  const parentIds = uniq(
    selectedAssemblies
      .map((assembly) => assembly.variation_parent_id)
      .filter((id): id is number => id != null),
  ).sort((a, b) => a - b);
  const parentIdsJoinString = parentIds.join('|');

  const { data: variationAssemblies = [] } = useQuery({
    queryKey: queryKeys.assemblies.all([
      `variation_parent_id.list=${assembliesIdsJoinString}`,
      'pageSize=0',
    ]),
    queryFn: () =>
      fetchAssembliesByIdsChunked('variation_parent_id', sortedIds),
    staleTime: Infinity,
    enabled: sortedIds.length > 0,
  });

  const { data: parentAssemblies = [] } = useQuery({
    queryKey: queryKeys.assemblies.all([
      `id.list=${parentIdsJoinString}`,
      'pageSize=0',
    ]),
    queryFn: () => fetchAssembliesByIdsChunked('id', parentIds),
    staleTime: Infinity,
    enabled: parentIds.length > 0,
  });

  const combinedAssembliesList = uniqBy(
    [...selectedAssemblies, ...variationAssemblies, ...parentAssemblies],
    (assembly) => assembly.id,
  );

  const allAssemblyIds = combinedAssembliesList?.map((item) => item.id) ?? [];

  return {
    allAssemblyIds,
  };
};
